// AST for the s-expression DSL. Every node
// carries a SourcePos populated by the parser; downstream consumers
// (type-checker, evaluator, error reporter) treat positions as mandatory.

export type SourcePos = {
  readonly line: number; //   1-based line number of the first character
  readonly col: number; //    1-based column number of the first character
  readonly offset: number; // 0-based byte offset of the first character
  readonly length: number; // byte length of the token range
};

/**
 * Sentinel position for diagnostics that cannot be attributed to a source
 * span — typically because the input was a direct JS object rather than a
 * parsed string, or because the error is structural and predates parsing
 * (e.g., empty script).
 */
export const NO_POS: SourcePos = { line: 0, col: 0, offset: 0, length: 0 };

export type LiteralNode = {
  readonly kind: 'literal';
  readonly value: number | string | boolean | null;
  readonly pos: SourcePos;
};

export type SymbolNode = {
  readonly kind: 'symbol';
  readonly name: string;
  readonly pos: SourcePos;
};

/**
 * Keyword node. Read at the lexer level from a `:` prefix; emitted by
 * the parser anywhere a value is permitted (vector element, op arg,
 * map key, head position of a call). Distinguished from `SymbolNode`
 * so the evaluator can treat keywords as first-class Values with their
 * own dispatch semantics (head-position keyword call becomes a
 * polymorphic `col` invocation; non-head keyword evaluates to a
 * `Keyword` Value).
 */
export type KeywordNode = {
  readonly kind: 'keyword';
  readonly name: string;
  readonly pos: SourcePos;
};

/**
 * An argument to a list call. Single-variant by design: kwarg vs
 * positional disambiguation is schema-directed and happens at
 * `parseOpCall` time, not in the parser. Every keyword the parser sees
 * is emitted as a positional KeywordNode; the validator lifts trailing
 * `:k v` pairs into kwargs based on the op schema's declared kwarg
 * keys.
 */
export type AstArg = { readonly kind: 'positional'; readonly value: AstNode };

export type ListNode = {
  readonly kind: 'list';
  readonly op: string; //         head symbol, separated from args at parse time
  readonly opPos: SourcePos; //   position of the head symbol (for op-targeted diagnostics)
  readonly args: readonly AstArg[];
  readonly pos: SourcePos; //     span covering the opening ( through the closing )
};

export type VectorNode = {
  readonly kind: 'vector';
  readonly elements: readonly AstNode[];
  readonly pos: SourcePos; //     span covering the opening [ through the closing ]
};

/**
 * Map literal node — `{:k v :k v}`. Keys are restricted to keywords;
 * arbitrary-character keys go through the `(hash-map …)` constructor
 * op instead of the literal surface. Entries preserve source order —
 * the evaluator builds a `MapValue` in the same order.
 */
export type MapNode = {
  readonly kind: 'map';
  readonly entries: readonly { readonly key: KeywordNode; readonly value: AstNode }[];
  readonly pos: SourcePos; //     span covering the opening { through the closing }
};

export type AstNode = LiteralNode | SymbolNode | KeywordNode | ListNode | VectorNode | MapNode;
